import { StepProps } from '../../types';
import { Stack } from '@startuptools/ui/Stack';
import { Trans, useLingui } from '@lingui/react';
import { Button } from '@startuptools/ui/joy/Button';
import { Controller } from 'react-hook-form';
import { Select } from '@startuptools/ui/joy/Select';
import { Option } from '@startuptools/ui/joy/Option';
import { UpdateField } from '../UpdateField';
import { getNewEndDateOfFiscalYear } from '@startuptools/common/aoa/fiscal-year';
import { GqlArticlesOfAssoc, GqlFiscalYear } from '../../../../graphql/base-types.graphql';
import { Alert } from '@startuptools/ui/joy/Alert';
import { FiscalYearDisplay } from '@startuptools/business-ui/FiscalYearDisplay';
import { DateFormat } from '@startuptools/ui/DateFormat';

export const StepFiscalYear = ({
  form,
  onSave,
  loading,
  aoa,
}: StepProps & { aoa: Pick<GqlArticlesOfAssoc, 'fiscalYearBeforeStart' | 'fiscalYearBefore' | 'fiscalYear'> }) => {
  const { i18n } = useLingui();
  const { fiscalYearBefore } = aoa;
  if (!fiscalYearBefore) {
    return (
      <Stack gap={2}>
        <Alert>
          <Trans id="These articles of associations were created before updating the fiscal year was supported. If you want to update the fiscal year, create a new articles of association." />
        </Alert>
        <Trans
          id="Fiscal year: {fiscalYear}"
          values={{ fiscalYear: <FiscalYearDisplay fiscalYear={aoa.fiscalYear} /> }}
        />
      </Stack>
    );
  }

  const newFiscalYear = form.watch('fiscalYear');
  const newEndDate =
    newFiscalYear && form.watch('changeFiscalYear')
      ? getNewEndDateOfFiscalYear(fiscalYearBefore, newFiscalYear)
      : undefined;
  return (
    <form onSubmit={form.handleSubmit(({ changeFiscalYear, fiscalYear }) => onSave({ changeFiscalYear, fiscalYear }))}>
      <Stack gap={2}>
        <Controller
          control={form.control}
          name="changeFiscalYear"
          key="changeFiscalYear"
          render={({ field }) => (
            <UpdateField value={field.value} onChange={field.onChange} label={<Trans id="Fiscal year" />} />
          )}
        />

        <Controller
          control={form.control}
          name="fiscalYear"
          key="fiscalYear"
          render={({ field }) => {
            return (
              <Select
                sx={{ maxWidth: '300px' }}
                value={field.value}
                onChange={(_, value) => field.onChange(value)}
                placeholder={<Trans id="Select fiscal year" />}
              >
                {[
                  GqlFiscalYear.JanToDec,
                  GqlFiscalYear.FebToJan,
                  GqlFiscalYear.MarToFeb,
                  GqlFiscalYear.AprToMar,
                  GqlFiscalYear.MayToApr,
                  GqlFiscalYear.JunToMay,
                  GqlFiscalYear.JulToJun,
                  GqlFiscalYear.AugToJul,
                  GqlFiscalYear.SepToAug,
                  GqlFiscalYear.OctToSep,
                  GqlFiscalYear.NovToOct,
                  GqlFiscalYear.DecToNov,
                ].map(fiscalYear => (
                  <Option key={fiscalYear} value={fiscalYear}>
                    <FiscalYearDisplay fiscalYear={fiscalYear} />
                  </Option>
                ))}
              </Select>
            );
          }}
        />

        {newEndDate && (
          <Alert>
            <Trans
              id="The current fiscal year will end on {date}"
              values={{
                date: (
                  <DateFormat inline locale={i18n.locale}>
                    {newEndDate}
                  </DateFormat>
                ),
              }}
            />
          </Alert>
        )}

        <Stack direction="row" justifyContent="flex-start">
          <Button type="submit" loading={loading}>
            <Trans id="Next" />
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
